import styled from '@emotion/styled';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, { FC, useEffect, useRef } from 'react';
import { useState } from 'react';
import { Icon } from 'src/shared/ds/components/Icon';
import { BodyTextMedium, HeadingText50 } from 'src/shared/ds/Typography';
import { dsColors } from 'src/shared/ds/variables';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { pxToRem } from 'src/shared/helpers/pxToRem';

interface ModalProps {
    title: string;
    description: string;
    handleClose: VoidFunction;
}

export enum ModalTestID {
    CloseModal = 'ModalTestID.CloseModal',
}

export const Modal: FC<ModalProps> = ({ title, description, handleClose, children }) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const [scrollPos, setScrollPos] = useState(0);

    useEffect(() => {
        if (modalRef.current) {
            setScrollPos(window.scrollY);
            disableBodyScroll(modalRef.current);
        }

        return () => {
            clearAllBodyScrollLocks();
        };
    }, [modalRef]);

    return (
        <Overlay css={{ top: scrollPos }}>
            <ModalContainer ref={modalRef}>
                <CloseContainer onClick={handleClose} data-testid={ModalTestID.CloseModal}>
                    <CloseButton name="close" size={32} />
                </CloseContainer>
                <HeadingText50>{title}</HeadingText50>
                <BodyTextMedium marginTop={12}>{description}</BodyTextMedium>
                <Content>{children}</Content>
            </ModalContainer>
        </Overlay>
    );
};

const Overlay = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: hexToRgba(dsColors.neutral300, 0.6),
    width: '100vw',
    height: '100vh',
});

const ModalContainer = styled.div({
    padding: `${pxToRem(64)} ${pxToRem(32)}`,
    borderRadius: pxToRem(20),
    backgroundColor: dsColors.white,
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 100,
});

const CloseContainer = styled.div({
    cursor: 'pointer',
});

const CloseButton = styled(Icon)({
    position: 'absolute',
    top: pxToRem(16),
    right: pxToRem(16),
});

const Content = styled.div({
    marginTop: pxToRem(16),
});
